import * as swcHelpers from '@swc/helpers';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Helpers } from '@grandvisionhq/inventory';
import { getNestedProperty, getLocalizedValue, getLocalizedAttrValue, getAttrValue, isPresent, getLocalizedAttrValues, getJoinedLocalizedAttrValues } from '@grandvisionhq/utils';
import { getDiscount } from './utils/get-discount.js';
import { getProductTag } from './utils/get-product-tag.js';

// using hardcoded english locale here to map:
// https://github.com/GrandVisionHQ/gv-core-components/blob/69612677a059e714191bee6c94b62f7866e039f1/packages/common/src/ColorPicker/color-mapping.tsx#L1
const COLOR_MAPPING_LOCALE = 'en-GB';
function getDiscountText(locale, variant) {
    if (!Array.isArray(variant.prices) || !variant.prices.length) {
        return '';
    }
    const priceObject = variant.prices[0];
    const description = getNestedProperty(priceObject, 'discounted.discount.obj.description');
    return getLocalizedValue(description, locale);
}
const getExtrasId = (product)=>{
    if (!product || !product.masterVariant || !Array.isArray(product.masterVariant.attributes)) {
        return undefined;
    }
    const extrasAttribute = product.masterVariant.attributes.find((attr)=>attr.name === 'lensExtras'
    );
    if (!extrasAttribute || !extrasAttribute.value) {
        return undefined;
    }
    return extrasAttribute.value.id;
};
function getPrice(variant) {
    if (!Array.isArray(variant.prices) || !variant.prices.length) {
        return undefined;
    }
    const price = variant.prices[0];
    if (!price.discounted) {
        return {
            originalPrice: price.value.centAmount,
            currency: price.value.currencyCode
        };
    }
    const discounted = price.discounted;
    return {
        currency: price.value.currencyCode,
        originalPrice: price.value.centAmount,
        discountedPrice: discounted.value.centAmount
    };
}
function getProductTypeName(product) {
    const result = product && product.productType && product.productType.obj && CommerceToolsApi.isProductTypeName(product.productType.obj.name) ? product.productType.obj.name : false;
    return !!result ? result : undefined;
}
function getProductModelName(variant, locale, productType) {
    if (CommerceToolsApi.isLenses(productType)) {
        return getLocalizedAttrValue(variant, 'materialType', locale);
    }
    return variant.name || '';
}
function getActiveVariant(product, sku) {
    if (!sku) {
        return product.masterVariant;
    }
    if (product.masterVariant.sku === sku) {
        return product.masterVariant;
    }
    return product.variants.find((variant)=>variant.sku === sku
    ) || product.masterVariant;
}
function getAvailability(variant) {
    return variant.availability ? {
        availableQuantity: Helpers.getAvailableQuantity(variant.availability)
    } : {};
}
const getLensPropertyValue = (variant, attrName)=>{
    const value = getAttrValue(variant, attrName);
    // We only care about numbers and strings
    if (!isPresent(value) || !(typeof value === 'number' || typeof value === 'string')) {
        return undefined;
    }
    return String(value);
};
function getSunglassesFeatureValue(variant, attrName, attrType) {
    const value = getAttrValue(variant, attrName);
    // We want to only return a value of type number in case that's the requested attr type.
    if (attrType === 'number') {
        return typeof value === 'number' ? value : undefined;
    }
    // We want to only return a value of type boolean in case that's the requested attr type.
    if (attrType === 'boolean') {
        return typeof value === 'boolean' ? value : undefined;
    }
    return undefined;
}
function getVariantInfo(product, productType, locale, variant) {
    const activeVariant = !variant || typeof variant === 'string' ? getActiveVariant(product, variant) : variant;
    const allowBackOrderAttribute = getAttrValue(activeVariant, 'allowBackOrder');
    const allowBackOrder = typeof allowBackOrderAttribute === 'undefined' ? undefined : !!allowBackOrderAttribute;
    const sharedProductInfo = {
        sku: activeVariant.sku || '',
        modelName: getProductModelName(activeVariant, locale, productType),
        header: getLocalizedAttrValue(activeVariant, 'header', locale),
        description: getLocalizedAttrValue(activeVariant, 'description', locale),
        images: getFullImageUrls(activeVariant),
        usps: getLocalizedAttrValues(activeVariant, 'usps', locale) || [],
        price: getPrice(activeVariant),
        brand: String(getAttrValue(activeVariant, 'brand')),
        discountText: getDiscountText(locale, activeVariant),
        discounted: getDiscount(activeVariant, locale),
        tag: getProductTag(activeVariant, locale),
        allowBackOrder,
        excludedFromCheckout: !!getAttrValue(activeVariant, 'excludedFromCheckout'),
        channelAvailability: activeVariant.channelAvailability,
        isPublished: activeVariant.isPublished !== false
    };
    var _isOfferedWithPrescriptions;
    const sharedFrameInfo = {
        name: activeVariant.name || '',
        frameColorLabel: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', locale),
        frameColor: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', COLOR_MAPPING_LOCALE),
        frameShape: getLocalizedAttrValue(activeVariant, 'frameShape', locale),
        frameMaterial: getLocalizedAttrValue(activeVariant, 'frameMaterial', locale),
        gender: getLocalizedAttrValues(activeVariant, 'gender', locale) || [
            'UNISEX'
        ],
        isKids: false,
        isOfferedWithPrescriptions: (_isOfferedWithPrescriptions = activeVariant.isOfferedWithPrescriptions) !== null && _isOfferedWithPrescriptions !== void 0 ? _isOfferedWithPrescriptions : false,
        sku: activeVariant.sku || ''
    };
    const genderKeys = getLocalizedAttrValues(activeVariant, 'gender', locale);
    if (genderKeys && Array.isArray(genderKeys) && genderKeys.length) {
        sharedFrameInfo.isKids = genderKeys.some((a)=>a.toLowerCase() === 'kids'
        );
    }
    // Contact-lenses
    if (CommerceToolsApi.isLenses(productType)) {
        const info = swcHelpers.objectSpread({}, sharedProductInfo, {
            productType: 'Contact-lenses',
            brand: getLocalizedValue(product.name, locale),
            replacementFrequency: getLocalizedAttrValue(activeVariant, 'replacementFrequency', locale),
            materialType: getLocalizedAttrValue(activeVariant, 'materialType', locale),
            lensType: getLocalizedAttrValue(activeVariant, 'lensType', locale),
            material: getLocalizedAttrValue(activeVariant, 'material', locale),
            waterContent: getLocalizedAttrValue(activeVariant, 'waterContent', locale),
            packageQuantity: Number(getAttrValue(activeVariant, 'packageQuantity')),
            features: {
                hydration: !!getAttrValue(activeVariant, 'cbHydration'),
                softness: !!getAttrValue(activeVariant, 'cbSoftness'),
                breathability: !!getAttrValue(activeVariant, 'cbBreathability'),
                fatigue: !!getAttrValue(activeVariant, 'cbFatigue'),
                uv: !!getAttrValue(activeVariant, 'cbUV'),
                basics: !!getAttrValue(activeVariant, 'cbBasics')
            },
            lensProperties: {
                power: getLensPropertyValue(activeVariant, 'power'),
                baseCurve: getLensPropertyValue(activeVariant, 'baseCurve'),
                diameter: getLensPropertyValue(activeVariant, 'diameter'),
                axis: getLensPropertyValue(activeVariant, 'axis'),
                cylinder: getLensPropertyValue(activeVariant, 'cylinder'),
                addition: getLensPropertyValue(activeVariant, 'addition'),
                color: getLensPropertyValue(activeVariant, 'color')
            },
            promotionalText: getLocalizedAttrValue(activeVariant, 'promotionalText', locale)
        });
        return info;
    // Sunglasses
    } else if (CommerceToolsApi.isSunglasses(productType)) {
        const availability = getAvailability(activeVariant);
        const info = swcHelpers.objectSpread({}, sharedProductInfo, sharedFrameInfo, {
            productType: 'Sunglasses',
            lensColor: getJoinedLocalizedAttrValues(activeVariant, 'lensColor', locale),
            features: {
                uvCategory: getSunglassesFeatureValue(activeVariant, 'UVCategory', 'number'),
                polarized: getSunglassesFeatureValue(activeVariant, 'polarized', 'boolean'),
                glazable: getSunglassesFeatureValue(activeVariant, 'glazeble', 'boolean')
            }
        }, {
            availability
        });
        return info;
    } else if (CommerceToolsApi.isAccessories(productType)) {
        const availability = getAvailability(activeVariant);
        const info = swcHelpers.objectSpread({}, sharedProductInfo, {
            productType: 'Accessories',
            accessoryColor: getJoinedLocalizedAttrValues(activeVariant, 'accessoryColor', locale),
            pricePerLiter: activeVariant.pricePerLiter
        }, {
            availability
        });
        return info;
    } else if (CommerceToolsApi.isSolutions(productType)) {
        const availability = getAvailability(activeVariant);
        return swcHelpers.objectSpread({}, sharedProductInfo, {
            contactLensCareType: getLocalizedAttrValue(activeVariant, 'contactLensCareType', locale),
            packageOption: String(getAttrValue(activeVariant, 'packageOption')),
            productType: 'Solutions',
            description: getLocalizedValue(product.description, locale),
            targetUsers: getLocalizedAttrValue(activeVariant, 'targetUsers', locale),
            convenience: getLocalizedAttrValue(activeVariant, 'convenience', locale),
            keyInstructions: getLocalizedAttrValue(activeVariant, 'keyInstructions', locale),
            features: {
                cleaning: getAttrValue(activeVariant, 'cbCleaning'),
                comfort: getAttrValue(activeVariant, 'cbComfort'),
                disinfect: getAttrValue(activeVariant, 'cbDisinfect'),
                hydration: getAttrValue(activeVariant, 'cbHydration'),
                rinsing: getAttrValue(activeVariant, 'cbRinsing'),
                sensitivity: getAttrValue(activeVariant, 'cbSensitivity')
            },
            pricePerLiter: activeVariant.pricePerLiter
        }, {
            availability
        });
    } else if (CommerceToolsApi.isReadyReaders(productType)) {
        const availability = getAvailability(activeVariant);
        return swcHelpers.objectSpread({}, sharedProductInfo, sharedFrameInfo, {
            productType: 'Ready-readers',
            features: {
                blueFilter: getAttrValue(activeVariant, 'blueFilter'),
                isOfferedWithPrescriptions: getAttrValue(activeVariant, 'isOfferedWithPrescriptions')
            }
        }, {
            availability
        });
    } else {
        const info = swcHelpers.objectSpread({}, sharedProductInfo, sharedFrameInfo, {
            productType: 'Frames',
            availability: getAvailability(activeVariant)
        });
        return info;
    }
}
var ref;
const getVariants = (variants, slug, productType, locale)=>{
    return variants.filter((variant)=>variant.isPublished !== false
    ).map((variant)=>{
        var ref1;
        return {
            name: `${variant.brand} ${variant.name}`,
            sku: variant.sku || '',
            slug,
            productType,
            description: getVariantDescription(variant, locale),
            imageUrl: (ref = (ref1 = getFullImageUrls(variant)[0]) === null || ref1 === void 0 ? void 0 : ref1.url) !== null && ref !== void 0 ? ref : ''
        };
    });
};
const getVariantDescription = (variant, locale)=>[
        'lensColor',
        'frameColor',
        'accessoryColor'
    ].map((attr)=>getJoinedLocalizedAttrValues(variant, attr, locale)
    ).filter((str)=>str.trim() !== ''
    ).join(' / ')
;
function getSunVariantPickerLabel(variant) {
    return [
        variant.lensColor,
        variant.frameColorLabel
    ].filter((str)=>str.trim() !== ''
    ).join(' / ');
}
function getProductAltTag(name, product, productType, locale) {
    const brand = getAttrValue(product, 'brand');
    const frameColor = getJoinedLocalizedAttrValues(product, 'frameColor', locale);
    const lensColor = getJoinedLocalizedAttrValues(product, 'lensColor', locale);
    if (productType && (CommerceToolsApi.isLenses(productType) || CommerceToolsApi.isContactLensBundle(productType))) {
        return typeof name === 'string' ? name : getLocalizedValue(name, locale);
    }
    const colorInfo = productType && CommerceToolsApi.isFrames(productType) || !lensColor ? frameColor : `${lensColor}${frameColor && ` / ${frameColor}`}`;
    return `${brand} ${product.name} ${colorInfo}`;
}
function getFullImageUrls(variant) {
    const images = getNestedProperty(variant, 'media.images', []);
    return images;
}
/**
 * This helper returns a composition of the
 * `brand`, `brandModel` en `brandModelCode` attributes for a variant
 */ const getProductTitle = (productType = '', product, variant, locale)=>{
    const brand = productType.toLowerCase() === 'contact-lenses' ? getLocalizedValue(product.name || '', locale) : String(getAttrValue(variant, 'brand'));
    var _masterVariant;
    const replacementFrequency = getLocalizedAttrValue((_masterVariant = product.masterVariant) !== null && _masterVariant !== void 0 ? _masterVariant : {}, 'replacementFrequency', locale) || '';
    const brandModel = getAttrValue(variant, 'brandModel') || '';
    const brandModelCode = getAttrValue(variant, 'brandModelCode') || '';
    const brandModelColorCode = getAttrValue(variant !== null && variant !== void 0 ? variant : {}, 'brandModelColorCode') || '';
    return `${replacementFrequency} ${brand} ${brandModel} ${brandModelCode !== brandModel ? brandModelCode : ''} ${brandModelColorCode}`.replace(/\s+/g, ' ').trim();
};

export { getActiveVariant, getDiscountText, getExtrasId, getFullImageUrls, getLensPropertyValue, getPrice, getProductAltTag, getProductModelName, getProductTitle, getProductTypeName, getSunVariantPickerLabel, getSunglassesFeatureValue, getVariantDescription, getVariantInfo, getVariants };
//# sourceMappingURL=helpers.js.map
